import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { LanguageType } from "../../CodeEditor/types";

import { getApiKeySteps } from "./getApiKeySteps";
import { ApiKeyStepperFormValues } from "./types";

import { generateNewApiKey } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { Permissions } from "@/providers/RbacProvider/constants";
import { useRbac } from "@/providers/RbacProvider";
import { useRefreshPromptLayerApiKeys } from "@/queries";
import { Workspace } from "@/types/workspaces";
import { displayErrorToast } from "@/utils/toast";

export const useApiKeyModal = () => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [keyName, setKeyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [language, setLanguage] = useState<LanguageType>("PYTHON");
  const authContext = useAuth()!;
  const userContext = useUser();
  const methods = useForm<ApiKeyStepperFormValues>({
    mode: "onChange",
    defaultValues: {
      api_key_name: "",
      api_key_result: "",
    },
  });

  const refreshPromptLayerApiKeys = useRefreshPromptLayerApiKeys();
  const { permissions } = useRbac();
  const cannotManageApiKeys = !permissions?.includes(Permissions.MANAGE_API_KEYS);

  const onCopyKey = () => {
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  const activeWorkspaceName = useMemo(() => {
    if (!authContext || !userContext) {
      return null;
    }

    const workspace = userContext.workspaces.find(
      ({ id }: Workspace) => id === userContext.activeWorkspaceId,
    );
    return workspace?.name ?? null;
  }, [authContext, userContext]);

  const resetDialog = () => {
    setActiveStep(0);
    setKeyName("");
    setApiKey(null);
    setIsCopied(false);
    setLanguage("PYTHON");
    setLoading(false);
    methods.reset();
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      resetDialog();
    }
  };

  const handleGenerateNewApiKey = async (name?: string) => {
    setLoading(true);

    try {
      const response = await generateNewApiKey(
        authContext,
        userContext?.activeWorkspaceId ?? 0,
        name,
      );

      if (response.status !== 200) {
        displayErrorToast("Failed to generate API key");
        return false;
      }

      const data = await response.json();
      if (!data.success) {
        displayErrorToast("Failed to generate API key");
        return false;
      }

      setApiKey(data.api_key);
      refreshPromptLayerApiKeys();
      return true;
    } catch {
      displayErrorToast("Failed to generate API key");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (loading || activeStep !== 0) {
      return;
    }

    const didGenerate = await handleGenerateNewApiKey(keyName.trim() || undefined);
    if (didGenerate) {
      setActiveStep(1);
    }
  };

  const codeBlock = useMemo(
    () =>
      language === "PYTHON"
        ? `from promptlayer import PromptLayer

  pl_client = PromptLayer(api_key=${apiKey || "<API_KEY>"})`
        : `import { PromptLayer } from "promptlayer";

  const promptLayerClient = new PromptLayer({ apiKey: "${apiKey || "<API_KEY>"}" });`,
    [apiKey, language],
  );

  const steps = getApiKeySteps({
    keyName,
    loading,
    activeWorkspaceName,
    apiKey,
    isCopied,
    language,
    codeBlock,
    onKeyNameChange: setKeyName,
    onGenerate: handleNext,
    onCopyKey,
    onLanguageChange: setLanguage,
  });

  return {
    methods,
    open,
    activeStep,
    loading,
    cannotManageApiKeys,
    steps,
    resetDialog,
    handleNext,
    handleDialogOpenChange,
    handleBack: () => {
      setActiveStep(Math.max(activeStep - 1, 0));
    },
  };
};
